import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import "./formPreview.css";
import NarrowInputField from "../../../components/input/new/narrowInputField";
import NarrowDropdown2 from "../../../components/input/new/narrowDropdown2";
import FileUploader from "../../../components/input/new/fileUploader";
import TextAreaWithCount from "../../../components/input/new/textAreaWithCount";
import IconCheckbox from "../../../components/input/new/iconCheckbox";
import RadioButton from "../../../components/input/new/radioButton";
import NarrowDatePicker from "../../../components/input/new/narrowDatePicker";
import { useSelector } from "react-redux";
import { authenticatedFetch } from "../../../network/api";

export default function FormPreview({ schema: propSchema }) {
  const location = useLocation();
  const navigate = useNavigate();
  const lawyerId = useSelector((s) => s.attorneyData.attorneyData?.user_id);
  const schema = propSchema || location.state?.schema;
  const formName = location.state?.formName || "AI Generated Intake Form";
  const description = location.state?.description || "";
  const appointmentType = location.state?.appointmentType;
  const requiredDocs = location.state?.requiredDocs;
  const [saving, setSaving] = useState(false);

  let form = schema;
  if (typeof form === "string") {
    try {
      form = JSON.parse(form);
    } catch (err) {
      return <p className="qp-no-form">Invalid form schema.</p>;
    }
  }
  if (!form || !Array.isArray(form.fields)) {
    return <div className="qp-no-form">No form schema available.</div>;
  }

  // Group fields by section
  const sections = form.fields.reduce((acc, field) => {
    const sec = field.section || "default";
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(field);
    return acc;
  }, {});

  // Render a field in preview (read-only) mode
  const renderField = (f) => {
    const commonProps = {
      id: f.id,
      name: f.id,
      value: f.value || "",
      placeholder: f.placeholder || "",
      readOnly: true,
      disabled: true,
    };
    switch (f.type) {
      case "file":
        return <FileUploader {...commonProps} disabled readOnly />;
      case "textarea":
        return <TextAreaWithCount {...commonProps} onChange={() => {}} />;
      case "select":
        return (
          <NarrowDropdown2
            {...commonProps}
            items={(f.options || []).map((opt) => ({ value: opt, label: opt }))}
            value={f.value || ""}
            onChange={() => {}}
          />
        );
      case "date":
        return <NarrowDatePicker {...commonProps} />;
      case "checkbox":
        return <IconCheckbox {...commonProps} checked={!!f.value} label={f.label} />;
      case "radio":
        return (
          <div className="qp-radio-group">
            {(f.options || []).map((opt) => (
              <RadioButton
                key={opt}
                {...commonProps}
                value={opt}
                checked={f.value === opt}
                label={opt}
              />
            ))}
          </div>
        );
      default:
        return <NarrowInputField {...commonProps} />;
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        name: formName,
        description,
        source: "ai",
        parsed_json: form,
        lawyerId,
      };
      // Optionally link to appointment type if provided
      if (appointmentType) body.linkToAppointmentTypes = [appointmentType];
      const res = await authenticatedFetch("/api/attorney/webapp/intake/forms/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const { error } = await res.json();
        alert("Failed to save template: " + (error || res.statusText));
      } else {
        // On success, navigate back to settings intake page
        navigate("/attorney/settings");
      }
    } catch (err) {
      alert("Something went wrong while saving the template.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = async () => {
    setSaving(true);
    try {
      const body = {
        name: formName,
        description,
        source: "ai",
        parsed_json: form,
        lawyerId,
      };
      if (appointmentType) body.linkToAppointmentTypes = [appointmentType];
      const res = await authenticatedFetch("/api/attorney/webapp/intake/forms/create", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const { error } = await res.json();
        alert("Failed to save template: " + (error || res.statusText));
        setSaving(false);
        return;
      }
      const { id } = await res.json();
      // Navigate to the form editor with the new templateId
      navigate("/attorney/intake/form-builder", { state: { templateId: id } });
    } catch (err) {
      alert("Something went wrong while saving the template.");
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="form-preview-container">
      <button className="form-preview-back" onClick={() => navigate(-1)}>&lt; Back</button>
      <div className="form-preview-header">
        <h2>Review AI-Generated Form</h2>
        <p>Here's a preview of your AI-generated intake form. You can edit it or save it directly.</p>
      </div>
      <div className="form-preview-main">
        <h1 className="form-preview-title">{form.title || formName || "Intake Form"}</h1>
        {Object.entries(sections).map(([secName, fields]) => (
          <div key={secName} className="form-preview-section">
            {secName !== "default" && (
              <h2 className="form-preview-section-title">{secName}</h2>
            )}
            {fields.map((f) => (
              <div key={f.id} className="form-preview-field">
                <label className="form-preview-label">
                  {f.label} {f.required && <span className="form-preview-required">*</span>}
                </label>
                {renderField(f)}
              </div>
            ))}
          </div>
        ))}
        <div className="form-preview-footer">
          <button className="form-preview-edit" onClick={handleEdit} disabled={saving}>
            {saving ? "Saving..." : "Edit This Form"}
          </button>
          <button className="form-preview-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving..." : "Save to Templates"}
          </button>
        </div>
      </div>
      <div className="form-preview-help-link">Need help?</div>
    </div>
  );
}
